import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import ReactModal from "react-modal";
import classNames from "classnames";
import { ContactUsFormProps } from "./types";
import { Translate } from "../../../locales/locales-utils";
import { RefButton } from "../../like-button-ref/like-button-ref";
import { formatPhoneNumber } from "../../../helpers/formatPhoneNumber";
import { RequestDataType } from "../../../types/demoRequestTypes";
import { API_SERVER_URL } from 'gatsby-env-variables-fork';
import {useBackendSettings} from '../../../layouts/useBackendSettings';

export const ContactUsForm = (props: ContactUsFormProps) => {
  const [textareaCount, textareaSetCount] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const backendSettings = useBackendSettings();

  const validationSchema = (needConfirmUserAgreement: boolean, needConfirmPersonDataProcessing: boolean) => {
    return yup.object().shape({
      name: yup
        .string()
        .strict()
        .trim(Translate("Global.ContactUsForm.ItemNameTrimmedValidationMessage"))
        .min(2, Translate("Global.ContactUsForm.ItemNameMinValidationMessage"))
        .max(20, Translate("Global.ContactUsForm.ItemNameMaxValidationMessage"))
        .matches(
          /^[a-zA-Zа-яА-Я'][a-zA-Zа-яА-Я-' ]+[a-zA-Zа-яА-Я']?$/u,
          Translate("Global.ContactUsForm.ItemNameAlphabetValidationMessage")
        )
        .required(
          Translate("Global.ContactUsForm.ItemNameRequiredValidationMessage")
        ),
      email: yup
        .string()
        .email(
          Translate("Global.ContactUsForm.ItemEmailInvalidValidationMessage")
        )
        .required(
          Translate("Global.ContactUsForm.ItemEmailRequiredValidationMessage")
        ),
      phoneNumber: yup
        .string()
        .min(
          18,
          Translate("Global.ContactUsForm.ItemPhoneInvalidValidationMessage")
        ),
      AgreementPolicy: yup.bool().oneOf([true]),
      UserAgreements: needConfirmUserAgreement
        ? yup.bool().oneOf([true])
        : yup.boolean(),
      PersonDataProcessing: needConfirmPersonDataProcessing
        ? yup.bool().oneOf([true])
        : yup.boolean(),
    });
  }

  const form = useFormik({
    initialValues: {
      name: "",
      email: "",
      phoneNumber: "",
      message: "",
      AgreementPolicy: false,
      UserAgreements: false,
      PersonDataProcessing: false
    },
    validateOnBlur: false,
    validateOnChange: true,
    validationSchema: validationSchema(
      backendSettings?.needConfirmUserAgreement ?? false,
      backendSettings?.needConfirmPersonDataProcessing ?? false
    ),
    onSubmit: async (values) => {
      const response = await fetch(`${API_SERVER_URL}/api/demo/add`, {
        credentials: "include",
        referrer: "/",
        method: "POST",
        cache: "no-cache",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
        body: JSON.stringify({
          UseTo: "Landings",
          FirstName: values.name || "",
          Email: values.email || "",
          Phone: values.phoneNumber || "",
          Data: values.message || "",
          Type: RequestDataType.ContactUs,
          RequestURL: window.location.href,
          AcceptedPrivacy: values.AgreementPolicy || false,
          AcceptedUserAgreement: values.UserAgreements || false,
          AcceptedPersonDataProcessing: values.PersonDataProcessing || false
        }),
      });
      if (response.ok) setShowSuccessMessage(true);
    },
  });

  function closeModal() {
    props.closeHandle();
    setShowSuccessMessage(false);
    form.resetForm();
  }

  return (
    <ReactModal
      isOpen={props.isOpen}
      bodyOpenClassName="modalWhiteBg modalForm"
    >
      <button className={"modalCloseBtn"} onClick={closeModal}></button>
      <form
        onSubmit={form.handleSubmit}
        noValidate
        className={showSuccessMessage ? "formWasSend" : ""}
      >
        {showSuccessMessage && (
          <div className="formMessage Success">
            <div className="formMessage-Icon">
              <svg
                width="80"
                height="80"
                viewBox="0 0 80 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="40" cy="40" r="40" fill="#65C15D" />
                <path
                  d="M30 38.3334L37.4274 46.5861C37.4665 46.6296 37.5344 46.6304 37.5745 46.5879L51.6667 31.6667"
                  stroke="white"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="h2 formMessageTitle">
              {Translate("Global.ContactUsForm.ItemSuccessTitle")}
            </div>
            <div className="h6 formMessageDescription">
              {Translate("Global.ContactUsForm.ItemSuccessText")}
            </div>
            <div className="formMessageActions">
              <RefButton
                text={Translate("Global.ContactUsForm.ItemSuccessButton")}
                variant="primary"
                size="lg"
                onClick={closeModal}
              />
            </div>
          </div>
        )}
        <div className="formHero">
          <div className="formHero-Title">
            {Translate("Global.ContactUsForm.Title")}
          </div>
        </div>
        <div
          className={classNames(
            "formControl",
            {
              ["formControlBrandingError"]:
                form.errors.name && form.touched.name,
            },
            {
              ["formControlBrandingSuccess"]:
                !form.errors.name && form.values.name,
            }
          )}
        >
          <label htmlFor="YouName">
            {Translate("Global.ContactUsForm.ItemNameLabel")}
            <span className="required-star">*</span>
          </label>
          <input
            type="text"
            id="YouName"
            name="name"
            placeholder={Translate("Global.ContactUsForm.ItemNamePlaceholder")}
            value={form.values.name}
            onBlur={form.handleBlur}
            onFocus={() =>
              form.setTouched({
                name: true,
                email: false,
                message: false,
                phoneNumber: false,
              })
            }
            onChange={form.handleChange}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(
                /[0-9|+|=|_|-|(|)|*|&|^|%|$|#|@|!|`|~|/.,|{|}|?|<|>|:|;|"|]/,
                ""
              );
            }}
          />
          {form.errors.name && form.touched.name && (
            <div className="formControlBrandingErrorMessage">
              {form.errors.name}
            </div>
          )}
        </div>
        <div
          className={classNames(
            "formControl",
            {
              ["formControlBrandingError"]:
                form.errors.email && form.touched.email,
            },
            {
              ["formControlBrandingSuccess"]:
                !form.errors.email && form.values.email,
            }
          )}
        >
          <label htmlFor="YouEmail">
            {Translate("Global.ContactUsForm.ItemEmailLabel")}
            <span className="required-star">*</span>
          </label>
          <input
            type="email"
            id="YouEmail"
            name="email"
            placeholder={Translate("Global.ContactUsForm.ItemEmailPlaceholder")}
            value={form.values.email}
            onBlur={form.handleBlur}
            onChange={form.handleChange}
            onFocus={() =>
              form.setTouched({
                name: false,
                email: true,
                message: false,
                phoneNumber: false,
              })
            }
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .trim()
                .replace(
                  /[+|=|(|)|*|&|^|%|$|#|!|`|~|/,|{|}|?|<|>|:|;|'"|]/,
                  ""
                );
            }}
          />
          {form.errors.email && form.touched.email && (
            <div className="formControlBrandingErrorMessage">
              {form.errors.email}
            </div>
          )}
        </div>
        <div
          className={classNames(
            "formControl",
            {
              ["formControlBrandingError"]:
                form.errors.phoneNumber && form.touched.phoneNumber,
            },
            {
              ["formControlBrandingSuccess"]:
                !form.errors.phoneNumber && form.values.phoneNumber,
            }
          )}
        >
          <label htmlFor="phoneNumber">
            {Translate("Global.ContactUsForm.ItemPhoneLabel")}
          </label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            placeholder={Translate("Global.ContactUsForm.ItemPhonePlaceholder")}
            value={form.values.phoneNumber}
            onBlur={form.handleBlur}
            maxLength={19}
            onChange={(e) => {
              form.setFieldValue(
                e.target.name,
                formatPhoneNumber(e.target.value, "+7 (000) 000-00-00")
              );
            }}
          />
          {form.errors.phoneNumber && form.touched.phoneNumber && (
            <div className="formControlBrandingErrorMessage">
              {form.errors.phoneNumber}
            </div>
          )}
        </div>
        <div className="formControl">
          <label htmlFor="Message" className="label-flex">
            {Translate("Global.ContactUsForm.ItemMessageLabel")}
            <span className="formTextAreaCounter">{textareaCount}/200</span>
          </label>
          <div className="formTextAreaContainer">
            <textarea
              id="Message"
              placeholder={Translate(
                "Global.ContactUsForm.ItemMessagePlaceholder"
              )}
              name="message"
              onChange={(e) => {
                textareaSetCount(e.target.value.length);
                form.handleChange("message")(e);
              }}
              maxLength={200}
              value={form.values.message}
            />
          </div>
        </div>
        <div className="formAgreements">
          <div
            className={classNames("formAgreementsContainer", {
              ["error"]:
              form.errors.AgreementPolicy && form.touched.AgreementPolicy,
            })}
          >
            <input
              checked={form.values.AgreementPolicy}
              onChange={(event) => {
                const { checked } = event.target;
                form.setFieldValue("AgreementPolicy", checked);
              }}
              type="checkbox"
              name="AgreementPolicy"
              id="AgreementPolicy"
            />
            <label htmlFor="AgreementPolicy">
              {backendSettings?.needConfirmUserAgreement
                ? Translate(
                  "Global.ContactUsExtendedForm.ItemAgreementsPrivacyPolicy"
                )
                : Translate("Global.ContactUsExtendedForm.ItemAgreements")}
              <span className="required-star">*</span>
            </label>
          </div>
          {backendSettings?.needConfirmUserAgreement && (
            <div
              className={classNames("formAgreementsContainer", {
                ["error"]:
                form.errors.UserAgreements && form.touched.UserAgreements,
              })}
            >
              <input
                checked={form.values.UserAgreements}
                onChange={(event) => {
                  const { checked } = event.target;
                  form.setFieldValue("UserAgreements", checked);
                }}
                type="checkbox"
                name="UserAgreements"
                id="UserAgreements"
              />
              <label htmlFor="UserAgreements">
                {Translate(
                  "Global.ContactUsExtendedForm.ItemAgreementsUserAgreements"
                )}
                <span className="required-star">*</span>
              </label>
            </div>
          )}
          {backendSettings?.needConfirmPersonDataProcessing && (
            <div
              className={classNames("formAgreementsContainer", {
                ["error"]:
                form.errors.PersonDataProcessing && form.touched.PersonDataProcessing,
              })}
            >
              <input
                checked={form.values.PersonDataProcessing}
                onChange={(event) => {
                  const { checked } = event.target;
                  form.setFieldValue("PersonDataProcessing", checked);
                }}
                type="checkbox"
                name="PersonDataProcessing"
                id="PersonDataProcessing"
              />
              <label htmlFor="PersonDataProcessing">
                {Translate(
                  "Global.ContactUsExtendedForm.ItemAgreementsPersonDataProcessing"
                )}
                <span className="required-star">*</span>
              </label>
            </div>
          )}
        </div>
        <div className="formActionsContactUs">
          <RefButton
            text={Translate("Global.BrandingForm.CancelButton")}
            variant="primary"
            outlined
            size="md"
            tag="button"
            onClick={closeModal}
          />
          <RefButton
            text={Translate("Global.ContactUsForm.ItemButtonText")}
            variant="primary"
            size="md"
            tag="button"
            type="submit"
          />
        </div>
      </form>
    </ReactModal>
  );
};
